import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { describeAxiosError, formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, FileJson, ChevronDown } from "lucide-react";
import { toast } from "sonner";

function Row({ label, amount, indent = 0, bold = false, negative = false, section = false, testid }) {
  const num = Number(amount || 0);
  // Auto-detect negative amounts (losses, refunds) so they never render as positives
  const showNegative = negative || num < 0;
  return (
    <tr className={section ? "bg-gray-100 font-semibold" : ""}>
      <td className={`py-1.5 ${bold ? "font-semibold" : ""}`} style={{ paddingLeft: `${indent * 20 + 8}px` }} data-testid={testid && `${testid}-label`}>
        {label}
      </td>
      <td className={`py-1.5 pr-3 text-right font-mono tabular-nums ${bold ? "font-semibold" : ""} ${showNegative ? "text-red-700" : ""}`} data-testid={testid && `${testid}-amount`}>
        {amount === null || amount === undefined || amount === "" ? "" : `${showNegative ? "− " : ""}₹ ${formatINR(Math.abs(num))}`}
      </td>
    </tr>
  );
}

function Inner() {
  const { activeId, active } = useCompany();
  const { tcid } = useParams();
  const navigate = useNavigate();
  const [comp, setComp] = useState(null);
  const [itrMenuOpen, setItrMenuOpen] = useState(false);
  const [itrBusy, setItrBusy] = useState(false);

  useEffect(() => {
    if (!activeId || !tcid) return;
    (async () => {
      try {
        const { data } = await api.get(`/companies/${activeId}/tax/computations/${tcid}`);
        setComp(data);
      } catch (err) {
        toast.error(describeAxiosError(err));
      }
    })();
  }, [activeId, tcid]);

  if (!comp) return <div className="p-6 text-sm text-gray-500">Loading working paper…</div>;
  const c = comp.computed || {};
  const profile = comp.profile_snapshot || c.profile || {};
  const heads = c.heads || {};
  const biz = heads.business_profession || {};

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <div className="print:hidden mb-4 flex items-center justify-between">
          <Button variant="outline" onClick={() => navigate(`/tax/computation/${tcid}`)} className="h-8">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Editor
          </Button>
          <div className="flex items-center gap-2 relative">
            <div className="relative">
              <Button
                variant="outline"
                onClick={() => setItrMenuOpen((o) => !o)}
                disabled={itrBusy}
                className="h-8"
                data-testid="btn-export-itr-json"
              >
                <FileJson className="w-4 h-4 mr-1" /> Export ITR JSON <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
              {itrMenuOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded shadow-lg z-10 w-64" data-testid="itr-menu">
                  {["ITR-1", "ITR-3", "ITR-4"].map((form) => (
                    <button
                      key={form}
                      onClick={async () => {
                        setItrMenuOpen(false);
                        setItrBusy(true);
                        try {
                          const { data } = await api.post(`/companies/${activeId}/tax/computations/${tcid}/itr-json?form=${form}`);
                          const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `${form}-${comp.ay}-${profile.pan || "computation"}.json`;
                          a.click();
                          URL.revokeObjectURL(url);
                          toast.success(`${form} JSON downloaded`);
                        } catch (err) {
                          toast.error(describeAxiosError(err));
                        } finally {
                          setItrBusy(false);
                        }
                      }}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 border-b border-gray-100 last:border-0 block"
                      data-testid={`btn-itr-${form.toLowerCase()}`}
                    >
                      <div className="font-semibold text-gray-900">{form}</div>
                      <div className="text-[10px] text-gray-500">
                        {form === "ITR-1" && "Salary + One House + Other Sources (≤ ₹50L)"}
                        {form === "ITR-3" && "Business / Profession (non-presumptive)"}
                        {form === "ITR-4" && "Presumptive 44AD / 44ADA (≤ ₹50L)"}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button onClick={() => window.print()} className="h-8 bg-blue-700 hover:bg-blue-800" data-testid="btn-print-now">
              <Printer className="w-4 h-4 mr-1" /> Print / Save PDF
            </Button>
          </div>
        </div>

        <div className="bg-white shadow print:shadow-none p-8 mx-auto" style={{ maxWidth: "800px" }} data-testid="tax-working-paper">
          <div className="text-center border-b-2 border-black pb-4 mb-4">
            <div className="uppercase text-xs tracking-widest text-gray-600 mb-1">Statement of Income</div>
            <div className="text-lg font-bold">{profile.name || active?.name || "—"}</div>
            {profile.pan && <div className="text-xs text-gray-700 font-mono mt-1">PAN: {profile.pan}</div>}
            <div className="text-sm mt-2">
              <span className="font-medium">AY {comp.ay}</span>
              <span className="mx-2">·</span>
              <span>FY {comp.fy_start} → {comp.fy_end}</span>
              <span className="mx-2">·</span>
              <span>{profile.taxpayer_type} · {profile.tax_regime} Regime · {profile.residential_status}</span>
            </div>
          </div>

          <table className="w-full text-sm">
            <tbody>
              <Row label="1. Income under the head Salaries" section />
              <Row label="Gross Salary" amount={heads.salary?.gross || 0} indent={1} />
              {heads.salary?.std_deduction > 0 && <Row label={`Less: Standard Deduction`} amount={heads.salary.std_deduction} indent={1} negative />}
              <Row label="Net Salary" amount={heads.salary?.net || 0} indent={1} bold />

              <Row label="2. Income from House Property" section />
              <Row label="Net Annual Value (after 30% ded. & interest u/s 24)" amount={heads.house_property?.net || 0} indent={1} bold />

              <Row label="3. Profits & Gains of Business or Profession" section />
              <Row label="Net Profit as per Books" amount={biz.net_profit_as_per_books || 0} indent={1} />
              {(biz.additions || []).map((a, i) => (
                <Row key={`add-${i}`} label={`Add: ${a.label || "Adjustment"}`} amount={a.amount} indent={2} />
              ))}
              {(biz.deductions || []).map((d, i) => (
                <Row key={`ded-${i}`} label={`Less: ${d.label || "Adjustment"}`} amount={d.amount} indent={2} negative />
              ))}
              {biz.it_act_depreciation > 0 && <Row label="Less: Depreciation as per IT Act (Sec 32)" amount={biz.it_act_depreciation} indent={2} negative />}
              <Row label="Net Business Income" amount={biz.net || 0} indent={1} bold />

              <Row label="4. Capital Gains" section />
              {heads.capital_gains?.stcg_normal > 0 && <Row label="STCG (slab-rated)" amount={heads.capital_gains.stcg_normal} indent={1} />}
              {heads.capital_gains?.stcg_111a > 0 && <Row label="STCG u/s 111A (equity STT, taxed @ 20%)" amount={heads.capital_gains.stcg_111a} indent={1} />}
              {heads.capital_gains?.ltcg_112a_gross > 0 && (
                <>
                  <Row label="LTCG u/s 112A — Gross" amount={heads.capital_gains.ltcg_112a_gross} indent={1} />
                  <Row label="Less: Exemption ₹1,25,000" amount={Math.min(heads.capital_gains.ltcg_112a_gross, heads.capital_gains.ltcg_112a_exemption)} indent={2} negative />
                </>
              )}
              {heads.capital_gains?.ltcg_other > 0 && <Row label="LTCG — Other assets (@ 12.5%)" amount={heads.capital_gains.ltcg_other} indent={1} />}
              <Row label="Net Capital Gains" amount={heads.capital_gains?.net || 0} indent={1} bold />

              <Row label="5. Income from Other Sources" section />
              <Row label="Interest / Dividend / Other" amount={heads.other_sources?.net || 0} indent={1} bold />

              <tr className="border-t-2 border-black">
                <td className="py-2 font-semibold uppercase text-xs tracking-wider">Gross Total Income</td>
                <td className="py-2 pr-3 text-right font-mono font-bold" data-testid="wp-gti">₹ {formatINR(c.gross_total_income || 0)}</td>
              </tr>

              {(c.setoff?.total || 0) > 0 && (
                <>
                  <Row label="Less: Set-off of current-year losses" amount={c.setoff.current_year} indent={1} negative />
                  <Row label="Less: Set-off of brought-forward losses" amount={c.setoff.brought_forward} indent={1} negative />
                </>
              )}

              {(c.chapter_via?.rows || []).length > 0 && (
                <>
                  <Row label="Less: Deductions under Chapter VI-A" section />
                  {c.chapter_via.rows.map((r) => (
                    <Row key={r.code} label={`${r.code} — ${r.title}`} amount={r.allowed} indent={1} negative />
                  ))}
                  <Row label="Total Chapter VI-A Deductions" amount={c.chapter_via.total} indent={0} bold negative />
                </>
              )}

              <tr className="border-t-2 border-black bg-blue-50">
                <td className="py-2 font-bold uppercase text-xs tracking-wider">Total Income (Taxable)</td>
                <td className="py-2 pr-3 text-right font-mono font-bold" data-testid="wp-total-income">₹ {formatINR(c.total_income || 0)}</td>
              </tr>

              <Row label="Computation of Tax" section />
              <Row label="Tax on slab-rated income" amount={c.tax?.slab || 0} indent={1} />
              {(c.tax?.stcg_111a || 0) > 0 && <Row label="Tax on STCG 111A @ 20%" amount={c.tax.stcg_111a} indent={1} />}
              {(c.tax?.ltcg_112a || 0) > 0 && <Row label="Tax on LTCG 112A @ 12.5%" amount={c.tax.ltcg_112a} indent={1} />}
              {(c.tax?.ltcg_other || 0) > 0 && <Row label="Tax on LTCG (other) @ 12.5%" amount={c.tax.ltcg_other} indent={1} />}
              <Row label="Tax before Rebate" amount={c.tax?.before_rebate || 0} indent={0} bold />
              {(c.tax?.rebate_87a || 0) > 0 && <Row label="Less: Rebate u/s 87A" amount={c.tax.rebate_87a} indent={1} negative />}
              <Row label="Tax after Rebate" amount={c.tax?.after_rebate || 0} indent={0} />
              {(c.tax?.surcharge || 0) > 0 && <Row label="Add: Surcharge" amount={c.tax.surcharge} indent={1} />}
              <Row label="Add: Health & Education Cess @ 4%" amount={c.tax?.cess || 0} indent={1} />

              <tr className="border-t-2 border-black">
                <td className="py-2 font-bold">Total Tax Payable</td>
                <td className="py-2 pr-3 text-right font-mono font-bold" data-testid="wp-total-tax">₹ {formatINR(c.tax?.total || 0)}</td>
              </tr>

              {(c.interest?.total || 0) > 0 && (
                <>
                  <Row label="Add: Interest u/s 234A" amount={c.interest?.["234a"] || 0} indent={1} />
                  <Row label="Add: Interest u/s 234B" amount={c.interest?.["234b"] || 0} indent={1} />
                  <Row label="Add: Interest u/s 234C" amount={c.interest?.["234c"] || 0} indent={1} />
                </>
              )}

              <Row label="Total Tax & Interest" amount={c.tax_and_interest || 0} indent={0} bold />

              <Row label="Less: Taxes Paid" section />
              {(c.tax_paid?.tds || 0) > 0 && <Row label="TDS" amount={c.tax_paid.tds} indent={1} negative />}
              {(c.tax_paid?.tcs || 0) > 0 && <Row label="TCS" amount={c.tax_paid.tcs} indent={1} negative />}
              {(c.tax_paid?.advance_tax || 0) > 0 && <Row label="Advance Tax" amount={c.tax_paid.advance_tax} indent={1} negative />}
              {(c.tax_paid?.self_assessment_tax || 0) > 0 && <Row label="Self-Assessment Tax" amount={c.tax_paid.self_assessment_tax} indent={1} negative />}
              <Row label="Total Taxes Paid" amount={c.tax_paid?.total || 0} indent={0} bold negative />

              <tr className={`border-t-2 border-black ${c.refund_or_payable > 0 ? "bg-red-50" : c.refund_or_payable < 0 ? "bg-emerald-50" : ""}`}>
                <td className="py-2 font-bold uppercase text-xs tracking-wider">
                  {c.refund_or_payable > 0 ? "Balance Tax Payable" : c.refund_or_payable < 0 ? "Refund Due" : "Nil"}
                </td>
                <td className="py-2 pr-3 text-right font-mono font-bold text-base" data-testid="wp-refund-payable">
                  ₹ {formatINR(Math.abs(c.refund_or_payable || 0))}
                </td>
              </tr>
            </tbody>
          </table>

          {comp.notes && (
            <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-700 whitespace-pre-wrap">
              <div className="uppercase text-[10px] tracking-wider text-gray-500 mb-1">Notes</div>
              {comp.notes}
            </div>
          )}

          <div className="mt-8 pt-4 border-t border-gray-300 text-[10px] text-gray-500 text-center">
            Generated by AXIORA Income Tax Module · Working Paper (AY {comp.ay})
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .print\\:shadow-none { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}

export default function TaxWorkingPaper() {
  return <RequireCompany><Inner /></RequireCompany>;
}
